/**
 * SourcesMethodsTab - Trust layer with source reliability and methods
 */

import React, { useState, useMemo } from 'react'
import { SourcesMethodsView, CIG } from '../../../lib/cockpit/schemas'
import { BlockRenderer } from '../blocks'
import { buildTextFragmentUrl } from '../../../lib/cockpit/textFragmentUrl'

interface SourcesMethodsTabProps {
  view: SourcesMethodsView
  cig: CIG
  viewMode?: 'mobile' | 'tablet' | 'desktop'
}

// Primary source types - official, regulatory, and authoritative documents
const PRIMARY_SOURCE_TYPES = [
  'primary',
  'regulation',
  'regulatory_filing',
  'regulatory_notice',
  'government_report',
  'government_guidance',
  'government',
  'official',
  'sec_filing',
  'peer_reviewed_study',
  'academic',
  'data'
]

// Source type categories for grouping
const SOURCE_TYPE_CATEGORIES: Record<string, { label: string; types: string[] }> = {
  regulatory: {
    label: 'Regulatory & Government',
    types: ['regulation', 'regulatory_filing', 'regulatory_notice', 'government_report', 'government_guidance', 'government', 'official', 'sec_filing']
  },
  academic: {
    label: 'Academic & Research',
    types: ['academic', 'peer_reviewed_study', 'research', 'study', 'journal', 'paper']
  },
  news: {
    label: 'News & Media',
    types: ['news', 'news_article', 'media', 'press', 'article']
  },
  market: {
    label: 'Market & Financial',
    types: ['market', 'market_report', 'financial', 'data', 'statistics', 'analytics']
  },
  industry: {
    label: 'Industry & Company',
    types: ['industry', 'company', 'corporate', 'business', 'report']
  },
  other: {
    label: 'Other Sources',
    types: []
  }
}

// Category icons as SVG components
const CategoryIcon = ({ category, className = "w-4 h-4" }: { category: string; className?: string }) => {
  switch (category) {
    case 'regulatory':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
        </svg>
      )
    case 'academic':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      )
    case 'news':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      )
    case 'market':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    case 'industry':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    default:
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
  }
}

// Helper to check if a source is considered "primary"
const isPrimarySource = (source: any): boolean => {
  const tier = (source.reliability_tier || '').toLowerCase()
  const type = (source.source_type || '').toLowerCase().replace(/\s+/g, '_')
  
  return tier === 'primary' || 
    PRIMARY_SOURCE_TYPES.some(t => type.includes(t) || tier.includes(t))
}

// Helper to categorize a source
const categorizeSource = (source: any): string => {
  const type = (source.source_type || '').toLowerCase().replace(/\s+/g, '_')
  const tier = (source.reliability_tier || '').toLowerCase()
  
  for (const [categoryKey, category] of Object.entries(SOURCE_TYPE_CATEGORIES)) {
    if (categoryKey === 'other') continue
    if (category.types.some(t => type.includes(t) || tier.includes(t))) {
      return categoryKey
    }
  }
  return 'other'
}

// Helper to extract domain from URL
const getDomainFromUrl = (url?: string): string | null => {
  if (!url) return null
  try {
    const urlObj = new URL(url)
    return urlObj.hostname.replace('www.', '')
  } catch {
    return null
  }
}

// Favicon component with fallback
const SourceFavicon = ({ url, title }: { url?: string; title: string }) => {
  const [hasError, setHasError] = useState(false)
  const domain = getDomainFromUrl(url)
  
  if (!domain || hasError) {
    // Fallback: first letter of title in a colored circle
    const initial = (title || 'S').charAt(0).toUpperCase()
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 
      'bg-teal-500', 'bg-pink-500', 'bg-indigo-500', 'bg-red-500'
    ]
    const colorIndex = title.charCodeAt(0) % colors.length
    
    return (
      <div className={`w-5 h-5 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${colors[colorIndex]}`}>
        {initial}
      </div>
    )
  }
  
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
      alt=""
      className="w-5 h-5 rounded flex-shrink-0"
      onError={() => setHasError(true)}
    />
  )
}

// Reliability Score with Tooltip - Fixed positioning with boundary detection
const ReliabilityBadge = ({ 
  score, 
  rationale, 
  tier,
  sourceType 
}: { 
  score: number
  rationale: string
  tier?: string
  sourceType?: string
}) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })
  const badgeRef = React.useRef<HTMLDivElement>(null)
  
  const percentage = Math.round(score * 100)
  const reliabilityColor = score >= 0.8 
    ? 'text-green-600 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
    : score >= 0.6 
    ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
    : score >= 0.4
    ? 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800'
    : 'text-red-600 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800'
  
  // Score breakdown that adds up correctly to the displayed percentage
  const getScoreBreakdown = () => {
    const factors: { label: string; value: number }[] = []
    let remaining = percentage
    
    // Base score (always 50% of the total)
    const baseScore = Math.round(percentage * 0.5)
    factors.push({ label: 'Base reliability score', value: baseScore })
    remaining -= baseScore
    
    // Source type contribution (20% of total if primary type, 15% otherwise)
    if (sourceType) {
      const typeLabel = sourceType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      const isPrimaryType = PRIMARY_SOURCE_TYPES.some(t => sourceType.toLowerCase().includes(t))
      const typeContribution = Math.round(percentage * (isPrimaryType ? 0.20 : 0.15))
      factors.push({ 
        label: `Source type: ${typeLabel}`, 
        value: typeContribution 
      })
      remaining -= typeContribution
    }
    
    // Publisher/classification contribution (remaining amount)
    if (tier) {
      const tierLabel = tier.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      factors.push({ 
        label: `Classification: ${tierLabel}`, 
        value: remaining 
      })
    } else if (remaining > 0) {
      factors.push({ 
        label: 'Publisher authority', 
        value: remaining 
      })
    }
    
    return factors
  }
  
  const handleMouseEnter = () => {
    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect()
      const tooltipWidth = 320
      const tooltipHeight = 260 // Approximate height
      
      // Calculate position with boundary detection
      let top = rect.bottom + 8
      let left = rect.left - (tooltipWidth / 2) + (rect.width / 2)
      
      // Keep tooltip within viewport horizontally
      if (left < 16) {
        left = 16
      } else if (left + tooltipWidth > window.innerWidth - 16) {
        left = window.innerWidth - tooltipWidth - 16
      }
      
      // If tooltip would go below viewport, show it above instead
      if (top + tooltipHeight > window.innerHeight - 16) {
        top = rect.top - tooltipHeight - 8
      }
      
      // If it would go above viewport, just show it at the top
      if (top < 16) {
        top = 16
      }
      
      setTooltipPosition({ top, left })
    }
    setShowTooltip(true)
  }
  
  const breakdown = getScoreBreakdown()
  const total = breakdown.reduce((sum, f) => sum + f.value, 0)
  
  return (
    <div 
      ref={badgeRef}
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span className={`px-2 py-0.5 text-xs font-medium rounded border cursor-help ${reliabilityColor}`}>
        {percentage}%
      </span>
      
      {/* Tooltip - fixed position with boundary detection */}
      {showTooltip && (
        <div 
          className="fixed z-[9999] w-80 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-2xl pointer-events-none"
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-sm text-gray-100">Reliability Score</div>
            <div className={`text-lg font-bold ${
              score >= 0.8 ? 'text-green-400' : score >= 0.6 ? 'text-blue-400' : score >= 0.4 ? 'text-yellow-400' : 'text-red-400'
            }`}>
              {percentage}%
            </div>
          </div>
          
          {/* Visual score bar */}
          <div className="mb-3">
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${
                  score >= 0.8 ? 'bg-green-500' : score >= 0.6 ? 'bg-blue-500' : score >= 0.4 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${percentage}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-gray-500 mt-1">
              <span>Low</span>
              <span>Medium</span>
              <span>High</span>
            </div>
          </div>
          
          {/* Score breakdown */}
          <div className="mb-3">
            <div className="text-gray-400 text-[10px] uppercase tracking-wider mb-2">Score breakdown</div>
            <div className="space-y-1.5">
              {breakdown.map((factor, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-gray-300">{factor.label}</span>
                  <span className="text-green-400 font-medium">
                    {factor.value}%
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-1.5 border-t border-gray-700">
                <span className="text-gray-100 font-medium">Total</span>
                <span className={`font-bold ${
                  score >= 0.8 ? 'text-green-400' : score >= 0.6 ? 'text-blue-400' : score >= 0.4 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {total}%
                </span>
              </div>
            </div>
          </div>
          
          {/* Rationale */}
          {rationale && (
            <div className="pt-2 border-t border-gray-700">
              <div className="text-gray-400 text-[10px] uppercase tracking-wider mb-1">Assessment</div>
              <div className="text-gray-200 leading-relaxed">{rationale}</div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Source Card Component
const SourceCard = ({ 
  source, 
  isExpanded, 
  onToggle 
}: { 
  source: any
  isExpanded: boolean
  onToggle: () => void
}) => {
  const isPrimary = isPrimarySource(source)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-visible">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full p-3 text-left flex items-start gap-3"
      >
        {/* Citation number */}
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center font-mono text-xs text-gray-600 dark:text-gray-400">
          {source.number}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mt-0.5">
            <SourceFavicon url={source.url} title={source.title} />
            <h3 className="font-medium text-sm text-gray-900 dark:text-white line-clamp-1">
              {source.title}
            </h3>
          </div>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <ReliabilityBadge 
              score={source.reliability_score}
              rationale={source.reliability_rationale}
              tier={source.reliability_tier}
              sourceType={typeof source.source_type === 'string' ? source.source_type : undefined}
            />
            {isPrimary && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded flex items-center gap-0.5">
                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Primary
              </span>
            )}
            {source.publisher && (
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[150px]">
                {source.publisher}
              </span>
            )}
            {source.date && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {source.date}
              </span>
            )}
          </div>
        </div>

        <svg
          className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-700 pt-3">
          {/* Reliability rationale */}
          <div className="p-2.5 bg-gray-50 dark:bg-gray-800/50 rounded-lg mb-3">
            <h4 className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
              Reliability Assessment
            </h4>
            <p className="text-xs text-gray-700 dark:text-gray-300">
              {source.reliability_rationale}
            </p>
          </div>

          {/* Excerpts */}
          {source.excerpts && source.excerpts.length > 0 && (
            <div className="mb-3">
              <h4 className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-1.5">
                Key Excerpts
              </h4>
              <div className="space-y-1.5">
                {source.excerpts.slice(0, 2).map((excerpt: any) => (
                  <blockquote
                    key={excerpt.id}
                    className="pl-2.5 border-l-2 border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400 italic"
                  >
                    "{excerpt.text}"
                  </blockquote>
                ))}
              </div>
            </div>
          )}

          {/* Link -- use text fragment to scroll to the relevant excerpt */}
          {source.url && (() => {
            const anchorText = source.anchor_text || source.excerpts?.[0]?.text
            const href = buildTextFragmentUrl(source.url, anchorText) || source.url
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                View source
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            )
          })()}
        </div>
      )}
    </div>
  )
}

function SourcesMethodsTab({ view, cig: _cig, viewMode = 'desktop' }: SourcesMethodsTabProps) {
  const [expandedSource, setExpandedSource] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState<string>('all') // 'all' or category key
  const isMobile = viewMode === 'mobile'

  // Group sources by category
  const groupedSources = useMemo(() => {
    const groups: Record<string, any[]> = {}
    
    for (const categoryKey of Object.keys(SOURCE_TYPE_CATEGORIES)) {
      groups[categoryKey] = []
    }
    
    for (const source of view.sources) {
      const category = categorizeSource(source)
      groups[category].push(source)
    }
    
    // Sort each group by number
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => a.number - b.number)
    }
    
    return groups
  }, [view.sources])

  // Get categories that have sources
  const availableCategories = useMemo(() => {
    return Object.entries(groupedSources)
      .filter(([_, sources]) => sources.length > 0)
      .map(([key, sources]) => ({
        key,
        ...SOURCE_TYPE_CATEGORIES[key],
        count: sources.length
      }))
  }, [groupedSources])
  
  // Get filtered sources based on active filter
  const filteredSources = useMemo(() => {
    if (activeFilter === 'all') {
      return [...view.sources].sort((a, b) => a.number - b.number)
    }
    return groupedSources[activeFilter] || []
  }, [activeFilter, view.sources, groupedSources])

  const avgReliability = view.sources.length > 0
    ? Math.round((view.sources.reduce((sum: number, s: any) => sum + s.reliability_score, 0) / view.sources.length) * 100)
    : 0

  // Count primary sources using our improved detection
  const primarySourceCount = view.sources.filter(isPrimarySource).length
  
  // Count total claims supported across all sources
  const totalClaimsSupported = view.sources.reduce((sum, s: any) => 
    sum + (s.claims_supported?.length || 0), 0
  )

  return (
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 space-y-6">
      {/* Overview stats - only show stats with values */}
      <div className={`grid gap-4 ${isMobile ? 'grid-cols-2' : totalClaimsSupported > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {view.sources.length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Sources</div>
        </div>
        <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">
            {avgReliability}%
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Avg. Reliability</div>
        </div>
        <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
            {primarySourceCount}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Primary Sources</div>
        </div>
        {/* Only show Claims Supported if > 0 */}
        {totalClaimsSupported > 0 && (
          <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {totalClaimsSupported}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Claims Supported</div>
          </div>
        )}
      </div>

      {/* Filter tabs - horizontal scrollable row */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
        {/* All filter */}
        <button
          onClick={() => setActiveFilter('all')}
          className={`flex-shrink-0 px-3 py-1.5 text-sm rounded-full border transition-all flex items-center gap-1.5 whitespace-nowrap ${
            activeFilter === 'all'
              ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-transparent'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
          <span>All</span>
          <span className={`px-1.5 py-0.5 text-xs rounded-full ${
            activeFilter === 'all'
              ? 'bg-white/20 dark:bg-gray-900/20'
              : 'bg-gray-100 dark:bg-gray-700'
          }`}>
            {view.sources.length}
          </span>
        </button>
        
        {/* Category filters */}
        {availableCategories.map(category => (
          <button
            key={category.key}
            onClick={() => setActiveFilter(category.key)}
            className={`flex-shrink-0 px-3 py-1.5 text-sm rounded-full border transition-all flex items-center gap-1.5 whitespace-nowrap ${
              activeFilter === category.key
                ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-transparent'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
          >
            <CategoryIcon category={category.key} className="w-4 h-4" />
            <span>{category.label}</span>
            <span className={`px-1.5 py-0.5 text-xs rounded-full ${
              activeFilter === category.key
                ? 'bg-white/20 dark:bg-gray-900/20'
                : 'bg-gray-100 dark:bg-gray-700'
            }`}>
              {category.count}
            </span>
          </button>
        ))}
      </div>

      {/* Sources grid */}
      <section className="bg-white dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            {activeFilter === 'all' ? (
              <>
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                All Sources
              </>
            ) : (
              <>
                <CategoryIcon category={activeFilter} className="w-5 h-5 text-gray-500" />
                {SOURCE_TYPE_CATEGORIES[activeFilter]?.label || 'Sources'}
              </>
            )}
          </h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {filteredSources.length} source{filteredSources.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
          {filteredSources.map(source => (
            <SourceCard
              key={source.id}
              source={source}
              isExpanded={expandedSource === source.id}
              onToggle={() => setExpandedSource(expandedSource === source.id ? null : source.id)}
            />
          ))}
        </div>
        
        {filteredSources.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No sources found in this category
          </div>
        )}
      </section>

      {/* Methodology */}
      {view.methodology && (
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Methodology
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              {view.methodology.summary}
            </p>
            {view.methodology.search_strategy && (
              <div className="mb-3">
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Search Strategy
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {view.methodology.search_strategy}
                </p>
              </div>
            )}
            {view.methodology.analysis_approach && (
              <div className="mb-3">
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Analysis Approach
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {view.methodology.analysis_approach}
                </p>
              </div>
            )}
            {view.methodology.tools_used && view.methodology.tools_used.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Tools Used
                </h4>
                <div className="flex flex-wrap gap-1">
                  {view.methodology.tools_used.map((tool: string) => (
                    <span
                      key={tool}
                      className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Limitations */}
      {view.limitations_block && (
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Limitations & Open Questions
          </h2>
          <BlockRenderer
            block={view.limitations_block}
            showChrome={false}
          />
        </section>
      )}

      {/* Change Log */}
      {view.change_log_block && (
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Change Log
          </h2>
          <BlockRenderer
            block={view.change_log_block}
            showChrome={false}
          />
        </section>
      )}
    </div>
  )
}

export default SourcesMethodsTab
